import {format, isSameDay} from 'date-fns'
import es from 'date-fns/locale/es'
import {Dispatch, FC, SetStateAction, useEffect, useMemo} from 'react'
import {Modal} from 'react-bootstrap'
import {Column} from 'react-table'
import {KTSVG} from '../../../../../../../../_metronic/helpers'
import {KTable, KTableHeader, KTableSelectionCell} from '../../../../../../components/UI'
import {useKTable} from '../../../../../../contexts/KTableProvider'
import {ShowSnackBar} from '../../../../../../util/functions'
import {ServiceSchedulingDto} from '../../../../model/cleaningSchedule.model'
import {CLEANING_SCHEDULE_SERVICES} from '../../../../services'
import {SCHEDULE_ACTIVITY_SERVICES} from '../../../../services/schedule-activity.service'

interface Props {
  show: boolean
  setShow: Dispatch<SetStateAction<boolean>>
  activityConfigId: string
  campamentoId: string
  schedules: string[]
  refreshTable: () => void
}

export const ModalAddSchedule: FC<Props> = ({
  show,
  setShow,
  activityConfigId,
  campamentoId,
  schedules,
  refreshTable,
}) => {
  const {
    data,
    setData,
    setPagination,
    setLoading,
    pagination,
    selectedOn,
    clearSelected,
    selected,
    onSelect,
  } = useKTable()

  const filteredSelected = useMemo(() => {
    const schedulesSet = new Set((schedules || []).map(String))

    return selected.filter((item) => !schedulesSet.has(String(item)))
  }, [selected, schedules]) // Lista de programaciones seleccionas filtrada, sin las que ya estan asignadas

  const columns: Column<ServiceSchedulingDto>[] = useMemo(
    () => [
      {
        Header: (props) => <KTableHeader tableProps={props} />,
        id: 'selection',
        Cell: ({...props}) => (
          <KTableSelectionCell
            id={props.data[props.row.index].Id}
            disable={props.data[props.row.index].asignado}
          />
        ),
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Campamento' />,
        accessor: 'Campamento',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Nomenclatura' />,
        accessor: 'Nomenclatura',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='TipoEdificacion' />,
        accessor: 'TipoEdificacion',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='TipoRepeticion' />,
        accessor: 'TipoRepeticion',
      },
      {
        Header: (props) => (
          <KTableHeader
            tableProps={props}
            title='Fecha - Hora'
            className='d-flex justify-content-center flex-shrink-0'
          />
        ),
        id: 'fechas',
        Cell: ({row}) => {
          const {FechaInicio, FechaFin, HoraInicio} = row.original || {}

          if (!FechaInicio) {
            return <div className='text-center text-muted'>Sin fecha</div>
          }

          const fechaInicio = new Date(FechaInicio)
          const fechaFin = FechaFin ? new Date(FechaFin) : null

          if (HoraInicio) {
            const [h = 0, m = 0, s = 0] = HoraInicio.split(':')
            fechaInicio.setHours(Number(h), Number(m), Number(s))
          }

          const mismoDia = fechaFin && isSameDay(fechaInicio, fechaFin)

          return (
            <div className='d-flex justify-content-center flex-shrink-0'>
              {fechaFin
                ? mismoDia
                  ? format(fechaInicio, 'dd MMM yyyy • HH:mm', {locale: es})
                  : `Del ${format(fechaInicio, 'dd MMM yyyy HH:mm', {locale: es})} al ${format(
                      fechaFin,
                      'dd MMM yyyy',
                      {locale: es},
                    )}`
                : format(fechaInicio, 'dd MMM yyyy HH:mm', {locale: es})}
            </div>
          )
        },
      },
    ],
    [selectedOn],
  )

  useEffect(() => {
    if (show === true) {
      clearSelected()
      getSchedulesByCampamentoList()
    } else {
      clearSelected() // Limpiar seleccionar al cerrar modal
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, show])

  const getSchedulesByCampamentoList = async () => {
    try {
      setLoading(true)
      const {data: response} = await CLEANING_SCHEDULE_SERVICES.filterCleaningSchedule({
        pageNumber: pagination.currentPage,
        size: pagination.perPage,
        CampamentoId: campamentoId,
      })
      if (!response.error) {
        const modifiedData = response.data.map((item) => {
          const asignado = schedules.includes(item.Id)
          if (asignado) {
            onSelect(item.Id)
          }
          return {...item, asignado}
        })
        // setData(response.data)
        setData(modifiedData)
        setPagination({
          ...pagination,
          totalResults: response.total,
        })
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setShow(false)
  }
  const onSubmit = async () => {
    try {
      setLoading(true)
      const {data: response} = await SCHEDULE_ACTIVITY_SERVICES.assigningActivities({
        ServiceSchedulingId: filteredSelected.map((x) => x.toString()),
        ActividadConfiguracionId: [activityConfigId],
      })
      console.log('response', response)
      if (!response.error) {
        handleClose()
        ShowSnackBar(response.message, 'success')
        refreshTable() // Refrescar tabla padre para mostrar nuevas programaciones asignadas
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  return (
    <Modal
      className='modal fade'
      show={show}
      dialogClassName='modal-xl'
      size='xl'
      aria-hidden='true'
      onHide={handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Programaciones</h5>
          <div className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={handleClose}>
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
          </div>
        </div>
        <div className='modal-body'>
          <KTable
            data={data}
            columns={columns as any}
            showButtonAdd={filteredSelected.length > 0}
            labelButtonAdd='Agregar'
            handleButtonAdd={onSubmit}
          />
        </div>
      </div>
    </Modal>
  )
}
